import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Crown, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { dashPath } from "../lib/utils-trel";
import Logo from "../components/Logo";

/**
 * Public invite-accept page for TREL Admin invitations.
 * Reads the invite token from the URL, surfaces invitee identity + permissions,
 * accepts a password, calls /api/admin/invites/{token}/accept, then auto-logs in
 * the new admin and routes to /admin.
 */
export default function AdminInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pwErr, setPwErr] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get(`/admin/invites/lookup/${token}`)
      .then(r => { if (mounted) setInvite(r.data); })
      .catch(e => {
        if (mounted) setError(e?.response?.data?.detail || "Invitation invalid or expired.");
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  const submit = async () => {
    setPwErr("");
    if (password.length < 8) return setPwErr("Password must be at least 8 characters.");
    if (password !== confirm) return setPwErr("Passwords do not match.");
    setSubmitting(true);
    try {
      await api.post(`/admin/invites/${token}/accept`, { password });
      // Auto-login the newly created admin
      const user = await login(invite.email, password);
      nav(dashPath(user.role));
    } catch (e) {
      setPwErr(e?.response?.data?.detail || "Could not accept invitation.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8EE] text-[#064E3B]">
        Loading invitation…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8EE] px-6">
        <div data-testid="invite-error" className="max-w-md w-full text-center rounded-3xl bg-white border border-[#FF7A45]/30 p-8 shadow-luxury">
          <AlertTriangle size={28} className="mx-auto text-[#FF7A45]" />
          <h1 className="font-serif text-2xl mt-4">Invitation unavailable</h1>
          <p className="text-sm text-[#071A2D]/70 mt-3">{error}</p>
          <Link to="/login" className="btn-outline-emerald mt-6 inline-block">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFF8EE] px-6 py-12">
      <div className="w-full max-w-lg" data-testid="invite-accept-card">
        <div className="flex items-center justify-between">
          <Link to="/"><Logo size="lg"/></Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
            TREL · Admin Invitation
          </span>
        </div>
        <div className="mt-8 rounded-3xl bg-white border border-[#C9A45C]/30 shadow-luxury p-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C9A45C]/15 flex items-center justify-center">
              <Crown size={18} className="text-[#C9A45C]"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
                You've been invited
              </div>
              <h1 className="font-serif text-2xl text-[#071A2D]">Join the TREL Admin team</h1>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Info label="Invitee">{invite.name || invite.email}</Info>
            <Info label="Email">{invite.email}</Info>
            {invite.invited_by_email && <Info label="Invited by">{invite.invited_by_email}</Info>}
            <Info label="Role">Admin</Info>
          </div>

          {(invite.permissions || []).length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-widest text-[#064E3B]/70 font-semibold flex items-center gap-2">
                <ShieldCheck size={12}/> Granted permissions
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {invite.permissions.map(p => (
                  <span key={p} className="text-[11px] px-2.5 py-1 rounded-full bg-[#064E3B]/8 text-[#064E3B]">
                    {p.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 border-t border-[#071A2D]/10 pt-6">
            <label className="text-xs uppercase tracking-widest text-[#064E3B]/70 flex items-center gap-2">
              <KeyRound size={12}/> Create your password
            </label>
            <input
              data-testid="invite-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="kbd-input mt-2"
              autoComplete="new-password"
            />
            <input
              data-testid="invite-password-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="kbd-input mt-3"
              autoComplete="new-password"
              placeholder="Confirm password"
            />
            <div className="text-[11px] text-[#071A2D]/55 mt-2">
              Min 8 chars, mixed case or a digit.
            </div>
            {pwErr && <div data-testid="invite-error-msg" className="text-sm text-[#FF7A45] mt-3">{pwErr}</div>}
            <button
              data-testid="invite-accept-submit"
              onClick={submit}
              disabled={submitting}
              className="btn-emerald w-full mt-5"
            >
              {submitting ? "Activating…" : "Accept invitation & sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, children }) => (
  <div className="rounded-xl border border-[#071A2D]/10 bg-[#FFF8EE] p-3">
    <div className="text-[10px] uppercase tracking-widest text-[#064E3B]/70">{label}</div>
    <div className="text-sm text-[#071A2D] mt-0.5 break-all">{children}</div>
  </div>
);
